import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogContent,
  IconButton,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useRef, useState } from "react";
import { tokens } from "../../../theme";
import StepHeader from "./StepHeader";

interface Props {
  open: boolean;
  onClose: () => void;
  phone: string;
  onVerify: (otp: string) => void;
  onResend: () => void;
  onBack: () => void;
  isSubmitting?: boolean;
}

export default function OtpVerificationForm({
  open,
  onClose,
  phone,
  onVerify,
  onResend,
  onBack,
  isSubmitting,
}: Props) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [otp, setOtp] = useState<string[]>(["", "", "", "", "", ""]);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace" && !otp[index] && index > 0)
      inputRefs.current[index - 1]?.focus();
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const pasted = e.clipboardData.getData("text").slice(0, 6).split("");
    if (pasted.every((c) => /\d/.test(c))) {
      const newOtp = [...otp];
      pasted.forEach((char, i) => {
        newOtp[i] = char;
      });
      setOtp(newOtp);
      inputRefs.current[Math.min(pasted.length, 5)]?.focus();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth={false}
      PaperProps={{
        sx: { borderRadius: "16px", overflow: "hidden", width: "350px" },
      }}
    >
      <IconButton
        onClick={onClose}
        sx={{
          position: "absolute",
          right: 10,
          top: 10,
          color: "#fff",
          zIndex: 10,
        }}
      >
        <CloseIcon />
      </IconButton>

      <StepHeader>
        <Typography
          variant="h6"
          fontWeight={600}
          color="#fff"
          textAlign="center"
        >
          Verify your email
        </Typography>
      </StepHeader>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.5,
            py: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: colors.primary[700], textAlign: "center" }}
          >
            Enter the 6 digit code sent to your email
          </Typography>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ color: colors.primary[600], mt: -0.5 }}
          >
            {phone}
          </Typography>

          <Box sx={{ display: "flex", gap: 1 }} onPaste={handlePaste}>
            {otp.map((digit, i) => (
              <input
                key={i}
                ref={(el) => {
                  inputRefs.current[i] = el;
                }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                style={{
                  width: "40px",
                  height: "44px",
                  textAlign: "center",
                  fontSize: "1.2rem",
                  fontWeight: 600,
                  border: "none",
                  borderRadius: "10px",
                  backgroundColor: colors.primary[800],
                  color: colors.primary[100],
                  outline: "none",
                  caretColor: "#724E41",
                }}
              />
            ))}
          </Box>

          <Typography
            variant="body2"
            sx={{ color: colors.primary[700], fontSize: "0.82rem" }}
          >
            Did not receive code?{" "}
            <span
              onClick={onResend}
              style={{ color: "#724E41", cursor: "pointer", fontWeight: 600 }}
            >
              Resend
            </span>
          </Typography>

          <Button
            variant="contained"
            size="medium"
            fullWidth
            disabled={otp.some((d) => !d) || isSubmitting}
            onClick={() => onVerify(otp.join(""))}
            sx={{
              backgroundColor: "#724E41",
              borderRadius: "24px",
              py: 1.2,
              mt: 0.5,
              width: "200px",
              "&:hover": { backgroundColor: "#4E342E" },
              "&.Mui-disabled": { backgroundColor: "#c4a99e", color: "#fff" },
            }}
          >
            Verify
          </Button>

          <Button
            size="small"
            onClick={onBack}
            disabled={isSubmitting}
            sx={{
              color: colors.primary[600],
              textTransform: "none",
              fontSize: "0.82rem",
              "&:hover": { backgroundColor: "transparent", color: "#724E41" },
            }}
          >
            ← Back
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
